import React, { useState, useEffect } from 'react';
import { can } from '../lib/access';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { 
  Search, FileText, TrendingUp, Flag, AlertCircle, 
  Bell, CheckCircle, ArrowUpRight, ArrowDownRight,
  User, Building2, ExternalLink, Shield, FolderOpen, Lock, UserMinus, Users, ArrowRight, GraduationCap
} from 'lucide-react';
import { guidesFor } from '../lib/guides';
import { startGuide } from '../components/WelcomeTour';
import ResolutionConfirmation from '../components/ResolutionConfirmation';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';

const SCOPE_NAMES = { me: 'Me', teams: 'My teams', organisation: 'Organisation' };

// One headline number with its change against the previous 30 days.
const Kpi = ({ label, value, suffix = '', previous, unit = '', percent = false, lowerIsBetter = false, note, warn, hint, onClick }) => {
  const has = value !== null && value !== undefined;
  let change = null;
  if (has && previous !== null && previous !== undefined) {
    const diff = percent ? (previous ? Math.round(((value - previous) / previous) * 100) : null) : Math.round((value - previous) * 10) / 10;
    if (diff !== null && diff !== 0) {
      const better = lowerIsBetter ? diff < 0 : diff > 0;
      change = <span style={{ color: better ? 'var(--qflow-accent)' : '#e0a052' }}>{diff > 0 ? '▲' : '▼'} {Math.abs(diff)}{percent ? '%' : unit} vs last month</span>;
    } else if (diff === 0) change = <span className="text-gray-500">Same as last month</span>;
  }
  const Body = onClick ? 'button' : 'div';
  return (
    <Body type={onClick ? 'button' : undefined} onClick={onClick} className={`qflow-home-card ${onClick ? 'qflow-home-tile' : ''} flex flex-col gap-1 p-4 text-left`}>
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <span className="qflow-home-number" style={warn ? { color: '#e0a052' } : undefined}>{has ? `${Number(value).toLocaleString()}${suffix}` : '–'}</span>
      <span className="text-xs">{note || change || <span className="text-gray-500">{hint || (has ? 'No earlier data' : 'Not enough data yet')}</span>}</span>
    </Body>
  );
};

// Weekly resolution rate as a simple line; weeks with no rated answers are left out.
const TrendLine = ({ points }) => {
  const rated = points.map((point, index) => ({ ...point, index })).filter((point) => point.rate !== null && point.rate !== undefined);
  if (rated.length < 2) return <p className="mt-6 text-sm text-gray-500">The trend appears once a couple of weeks of answers have been rated.</p>;
  const width = 320, height = 96, pad = 8;
  const x = (index) => pad + (index * (width - pad * 2)) / Math.max(points.length - 1, 1);
  // Zoom to the range the rate actually moved in, so a change from 71% to 82% is visible.
  const low = Math.max(0, Math.min(...rated.map((point) => point.rate)) - 10);
  const high = Math.min(100, Math.max(...rated.map((point) => point.rate)) + 5);
  const y = (rate) => height - pad - ((rate - low) / Math.max(high - low, 1)) * (height - pad * 2);
  const last = rated[rated.length - 1];
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="mt-3 h-28 w-full" role="img" aria-label={`Resolution rate over 8 weeks, now ${last.rate}%`}>
      {[low, high].map((rate) => <g key={rate}><line x1={pad} x2={width - pad} y1={y(rate)} y2={y(rate)} stroke="var(--qflow-border)" strokeWidth="1" /><text x={pad} y={y(rate) - 3} fontSize="9" fill="var(--qflow-muted)">{Math.round(rate)}%</text></g>)}
      <polyline fill="none" stroke="var(--qflow-accent)" strokeWidth="2.5" strokeLinejoin="round" points={rated.map((point) => `${x(point.index)},${y(point.rate)}`).join(' ')} />
      <circle cx={x(last.index)} cy={y(last.rate)} r="4" fill="var(--qflow-accent)" />
      <text x={x(last.index) - 6} y={y(last.rate) - 8} textAnchor="end" fontSize="11" fill="var(--qflow-muted)">{last.rate}%</text>
    </svg>
  );
};

const AssignmentDialog = ({ open, onOpenChange, card, users, onAssign, saving }) => {
  const [query, setQuery] = useState('');
  const matches = users.filter((candidate) =>
    (candidate.name || '').toLowerCase().startsWith(query.trim().toLowerCase())
    || (candidate.email || '').toLowerCase().startsWith(query.trim().toLowerCase())
  );

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{card?.assigned_to ? 'Reassign query' : 'Assign query'}</DialogTitle>
        </DialogHeader>
        <Input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search users by name or email"
          data-testid="query-assignee-search"
          autoFocus
        />
        <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 border-y border-gray-100">
          {matches.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              className="flex w-full items-center gap-3 px-2 py-3 text-left hover:bg-gray-50"
              onClick={() => onAssign(candidate.id)}
              disabled={saving}
              data-testid={`assign-query-user-${candidate.id}`}
            >
              {candidate.photo_url ? (
                <img src={candidate.photo_url} alt="" className="h-8 w-8 rounded-full object-cover" />
              ) : (
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-700">
                  {(candidate.name || '?').split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase()}
                </span>
              )}
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-gray-900">{candidate.name}</span>
                <span className="block truncate text-xs text-gray-500">{candidate.email}</span>
              </span>
            </button>
          ))}
          {matches.length === 0 && <p className="px-2 py-5 text-center text-sm text-gray-500">No matching users</p>}
        </div>
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-gray-600 hover:bg-gray-50"
          onClick={() => onAssign(null)}
          disabled={saving}
          data-testid="unassign-query"
        >
          <UserMinus size={15} /> Unassign
        </button>
      </DialogContent>
    </Dialog>
  );
};
// ============= METRIC DETAIL MODAL =============
const MetricDetailModal = ({ isOpen, onClose, title, icon: Icon, color, data, type, onNavigate }) => {
  if (!isOpen) return null;
  
  const colorClasses = {
    sky: 'bg-sky-50 text-sky-700 border-sky-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    purple: 'bg-purple-50 text-purple-700 border-purple-200',
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
              <Icon size={20} />
            </div>
            {title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {(type === 'queries' || type === 'answered') && (
            <>
              <p className="text-sm text-gray-500">
                {type === 'answered' ? 'Queries that received a Qflow response' : 'Your query history - all queries you have submitted'}
              </p>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {data?.length > 0 ? data.map((q, idx) => (
                  <div key={idx} className="px-3 py-2.5 bg-gray-50 rounded-md border">
                    <p className="font-medium text-gray-900 line-clamp-2">{q.query_text}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span>{new Date(q.created_at).toLocaleDateString()}</span>
                      <span className={`px-2 py-0.5 rounded ${
                        q.status === 'resolved' ? 'bg-green-100 text-green-700'
                        : q.status === 'unresolved' ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'
                      }`}>
                        {(q.status || 'pending_feedback').replace('_', ' ')}
                      </span>
                      <span className="capitalize">{q.knowledge_coverage || 'N/A'} coverage</span>
                    </div>
                  </div>
                )) : (
                  <p className="text-gray-400 text-center py-4">No queries yet</p>
                )}
              </div>
            </>
          )}
          
          {type === 'resolved' && (
            <>
              <p className="text-sm text-gray-500">Queries the user explicitly confirmed were resolved</p>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {data?.length > 0 ? data.map((q, idx) => (
                  <div key={idx} className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="font-medium text-gray-900 line-clamp-2">{q.query_text}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <CheckCircle size={12} className="text-green-600" />
                        Resolved {new Date(q.created_at).toLocaleDateString()}
                      </span>
                      {q.matched_resolutions?.length > 0 && (
                        <span>Source: {q.matched_resolutions[0]?.title}</span>
                      )}
                    </div>
                  </div>
                )) : (
                  <p className="text-gray-400 text-center py-4">No resolved queries yet</p>
                )}
              </div>
            </>
          )}
          
          {type === 'flagged' && (
            <>
              <p className="text-sm text-gray-500">Flagged queries and Floor Space escalations requiring human review.</p>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {data?.length > 0 ? data.map((card, idx) => {
                  const isEscalation = card.card_type === 'escalation';
                  const statusColor = card.status === 'resolved'
                    ? 'bg-green-100 text-green-700'
                    : card.status === 'in_review' || card.status === 'in_progress'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-amber-100 text-amber-700';
                  return (
                    <div key={card.id || idx} className={`p-3 rounded-lg border ${isEscalation ? 'bg-orange-50 border-orange-200' : 'bg-amber-50 border-amber-200'}`}>
                      <p className="font-medium text-gray-900 line-clamp-2">{card.query_text}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 flex-wrap">
                        <span className={`px-2 py-0.5 rounded font-medium ${isEscalation ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'}`}>
                          {card.source || (isEscalation ? 'Escalated' : 'Flagged')}
                        </span>
                        <span className={`px-2 py-0.5 rounded ${statusColor} capitalize`}>{(card.status || 'pending').replace('_', ' ')}</span>
                        {card.owner_name && (
                          <span className="flex items-center gap-1">
                            <User size={12} /> {card.owner_name}
                          </span>
                        )}
                        <span>{card.created_at ? new Date(card.created_at).toLocaleDateString() : ''}</span>
                      </div>
                      {card.reason && <p className="text-sm text-gray-600 mt-2 italic">&quot;{card.reason}&quot;</p>}
                    </div>
                  );
                }) : (
                  <p className="text-gray-400 text-center py-4">No query cards</p>
                )}
              </div>
            </>
          )}
          
          {type === 'resolution_rate' && (
            <>
              <p className="text-sm text-gray-500">Your personal resolution rate trend</p>
              {data?.trend && data.trend.length > 0 ? (
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.trend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="asked" stroke="#3B82F6" fill="#DBEAFE" name="Asked" />
                      <Area type="monotone" dataKey="resolved" stroke="#22C55E" fill="#DCFCE7" name="Resolved" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">Not enough data for trend</p>
              )}
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-700">{data?.total_answered || 0}</p>
                  <p className="text-xs text-blue-600">Total Answered</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-700">{data?.total_resolved || 0}</p>
                  <p className="text-xs text-green-600">Total Resolved</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-700">{Math.round(data?.rate || 0)}%</p>
                  <p className="text-xs text-purple-600">Resolution Rate</p>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4" onClick={() => onNavigate('/analytics')}>
                <TrendingUp size={16} className="mr-2" /> View Full Analytics
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============= NOTIFICATION SETTINGS MODAL =============
// ============= MAIN DASHBOARD COMPONENT =============
const timeAgo = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const Dashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  
  // Data states
  const [userStats, setUserStats] = useState(null);
  const [userQueries, setUserQueries] = useState([]);
  const [queryCards, setQueryCards] = useState([]);
  const [queryCardCounts, setQueryCardCounts] = useState({ total: 0, flagged: 0, escalations: 0, pending: 0 });
  const [queryCardRoles, setQueryCardRoles] = useState({ is_source_lead: false, is_floor_leader: false, is_admin: false });
  const [accessSummary, setAccessSummary] = useState(null); // null until fetched; { unrestricted, is_source_lead, folders, files }
  const [notifications, setNotifications] = useState([]);
  const [profileSummary, setProfileSummary] = useState({ teams_and_positions: [], leadership: {} });
  const [homeSummary, setHomeSummary] = useState({ roles: [], teams: [], floor_space: {}, knowledge_updates: [], suggestions: [], admin: null, knowledge_attention: null });
  const [passwordDialog, setPasswordDialog] = useState({ open: false, current: '', next: '', confirm: '', saving: false });
  const [loading, setLoading] = useState(true);
  const [snapshot, setSnapshot] = useState(null);
  // Me / My teams / Organisation, remembered per person on this device.
  const scopeKey = `qflow:home-scope:${user.id}`;
  const [scopeChoice, setScopeChoice] = useState(() => { try { return localStorage.getItem(scopeKey) || ''; } catch { return ''; } });
  const [snapshotError, setSnapshotError] = useState(false);
  const [ratingOpen, setRatingOpen] = useState(false);
  const canManageAssignments = can(user, 'can_assign_escalations');
  const [myQueue, setMyQueue] = useState(!canManageAssignments);
  const [assignmentUsers, setAssignmentUsers] = useState([]);
  const [assignmentDialog, setAssignmentDialog] = useState({ open: false, card: null });
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  
  // Modal states
  const [metricModal, setMetricModal] = useState({ isOpen: false, type: null, title: '', icon: null, color: '', data: null });

  useEffect(() => {
    fetchDashboardData();
  }, [user.id, myQueue]);

  useEffect(() => {
    let active = true;
    setSnapshot(null);
    setSnapshotError(false);
    axiosInstance.get('/dashboard/snapshot', { params: scopeChoice ? { scope: scopeChoice } : {} })
      .then(({ data }) => { if (active) setSnapshot(data); })
      .catch(() => { if (active) { setSnapshot(null); setSnapshotError(true); } });
    return () => { active = false; };
  }, [user.id, user.role, user.tenant_id, userQueries, scopeChoice]);
  const chooseScope = (value) => {
    setScopeChoice(value);
    try { localStorage.setItem(scopeKey, value); } catch {}
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch user's own queries
      const queriesRes = await axiosInstance.get('/queries/my');
      const myQueries = queriesRes.data || [];
      setUserQueries(myQueries);
      
      // Calculate user stats
      const answeredStatuses = new Set(['answered', 'pending_feedback', 'resolved', 'unresolved']);
      const answered = myQueries.filter(q => answeredStatuses.has(q.status));
      const resolved = myQueries.filter(q => q.status === 'resolved');
      const resolutionRate = answered.length > 0 ? Math.round((resolved.length / answered.length) * 100) : 0;
      
      // Generate trend data (last 7 days)
      const trend = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayQueries = myQueries.filter(q => q.created_at?.startsWith(dateStr));
        trend.push({
          date: date.toLocaleDateString('en-US', { weekday: 'short' }),
          asked: dayQueries.length,
          answered: dayQueries.filter(q => answeredStatuses.has(q.status)).length,
          resolved: dayQueries.filter(q => q.status === 'resolved').length
        });
      }
      
      setUserStats({
        total: myQueries.length,
        answered: answered.length,
        resolved: resolved.length,
        resolution_rate: resolutionRate,
        trend,
        total_answered: answered.length,
        total_resolved: resolved.length,
        rate: resolutionRate
      });
      
      // Fetch unified query cards (flagged queries + Floor Space escalations)
      try {
        const qcRes = await axiosInstance.get('/dashboard/query-cards', { params: { my_queue: myQueue } });
        setQueryCards(qcRes.data.cards || []);
        setQueryCardCounts(qcRes.data.counts || { total: 0, flagged: 0, escalations: 0, pending: 0 });
        setQueryCardRoles(qcRes.data.roles || {});
        if (qcRes.data.roles?.can_manage_assignments) {
          const usersRes = await axiosInstance.get('/users');
          setAssignmentUsers(usersRes.data || []);
        } else {
          setAssignmentUsers([]);
        }
      } catch (e) {
        setQueryCards([]);
      }

      // Fetch My Access summary (rendered for Source Leaders with restricted access)
      try {
        const accRes = await axiosInstance.get('/rbac/my-access-summary');
        setAccessSummary(accRes.data);
      } catch (e) {
        setAccessSummary(null);
      }
      
      // Fetch the signed-in user's tenant-scoped teams, Org Chart titles and leadership duties.
      try {
        const profileRes = await axiosInstance.get('/dashboard/my-profile-summary');
        setProfileSummary(profileRes.data || { teams_and_positions: [], leadership: {} });
      } catch (e) {
        setProfileSummary({ teams_and_positions: [], leadership: {} });
      }
      // Home sections: your roles, Floor Space, recent knowledge, starter questions, admin tasks.
      try {
        const homeRes = await axiosInstance.get('/dashboard/home-summary');
        setHomeSummary(homeRes.data || {});
      } catch (e) {
        setHomeSummary({ roles: [], teams: [], floor_space: {}, knowledge_updates: [], suggestions: [], admin: null, knowledge_attention: null });
      }
      // Fetch notifications
      try {
        const notifRes = await axiosInstance.get('/notifications');
        setNotifications(notifRes.data || []);
      } catch (e) {
        setNotifications([]);
      }
      
    } catch (error) {
      console.error('Failed to fetch dashboard data', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignQuery = async (assignedTo) => {
    const card = assignmentDialog.card;
    if (!card) return;
    setAssignmentSaving(true);
    try {
      await axiosInstance.patch(`/dashboard/query-cards/${card.id}/assignment`, { assigned_to: assignedTo });
      toast.success(assignedTo ? 'Query assigned' : 'Query unassigned');
      setAssignmentDialog({ open: false, card: null });
      await fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update query assignment');
    } finally {
      setAssignmentSaving(false);
    }
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    if (passwordDialog.next !== passwordDialog.confirm) { toast.error('New passwords do not match'); return; }
    setPasswordDialog((current) => ({ ...current, saving: true }));
    try {
      await axiosInstance.post('/auth/change-password', { current_password: passwordDialog.current, new_password: passwordDialog.next });
      toast.success('Password changed successfully');
      setPasswordDialog({ open: false, current: '', next: '', confirm: '', saving: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
      setPasswordDialog((current) => ({ ...current, saving: false }));
    }
  };
  const openMetricModal = (type, title, icon, color, data) => {
    setMetricModal({ isOpen: true, type, title, icon, color, data });
  };

  const markNotificationRead = async (notifId) => {
    try {
      await axiosInstance.patch(`/notifications/${notifId}/read`);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (error) {
      console.error('Failed to mark notification read');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }


  // Repeated questions show once, newest first.
  const uniqueByText = (queries) => {
    const seen = new Set();
    return [...queries].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).filter((query) => {
      const key = (query.query_text || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };
  const doneStatuses = new Set(['resolved', 'Resolved', 'closed']);
  const toRate = uniqueByText(userQueries.filter((query) => query.status === 'pending_feedback'));
  const myEscalations = queryCards.filter((card) => card.card_type === 'escalation' && card.assigned_to === user.id && !doneStatuses.has(card.status));
  const flaggedForMe = queryCardRoles.is_source_lead ? queryCards.filter((card) => card.card_type === 'flagged' && card.status === 'pending') : [];
  const attention = homeSummary.knowledge_attention;
  const unreadNotices = notifications.filter((notification) => !notification.read).slice(0, 2);
  const scrollToQueue = () => document.getElementById('escalated-queue')?.scrollIntoView({ behavior: 'smooth' });
  const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;
  // Role-aware tiles: each person only sees the work that is theirs.
  const tiles = [
    toRate.length && { key: 'rate', count: toRate.length, label: toRate.length === 1 ? 'answer to rate' : 'answers to rate', action: ratingOpen ? 'Hide' : 'Close the loop', onAction: () => setRatingOpen((current) => !current) },
    myEscalations.length && { key: 'escalations', count: myEscalations.length, label: myEscalations.length === 1 ? 'escalation for you' : 'escalations for you', action: 'Open', onAction: scrollToQueue },
    flaggedForMe.length && { key: 'flags', count: flaggedForMe.length, label: flaggedForMe.length === 1 ? 'flagged answer' : 'flagged answers', action: 'Review', onAction: scrollToQueue },
    attention?.total && { key: 'attention', count: attention.total, label: attention.total === 1 ? 'document needs review' : 'documents need review', meta: [attention.refresh_failed && `${attention.refresh_failed} refresh failed`, attention.review_due && `${attention.review_due} review due`, attention.expired && `${attention.expired} past end date`].filter(Boolean).join(' · ') || null, action: 'Review knowledge', onAction: () => navigate('/resolutions?attention=1') },
    homeSummary.admin?.pending_users && { key: 'approvals', count: homeSummary.admin.pending_users, label: homeSummary.admin.pending_users === 1 ? 'person waiting to join' : 'people waiting to join', action: 'Review', onAction: () => navigate('/team-users'), warn: true },
    homeSummary.floor_space?.available && homeSummary.floor_space?.open_questions && { key: 'floor', count: homeSummary.floor_space.open_questions, label: homeSummary.floor_space.open_questions === 1 ? 'Floor question you could answer' : 'Floor questions you could answer', action: 'Open Floor Space', onAction: () => navigate('/floor-space') },
  ].filter(Boolean);
  const notices = unreadNotices.map((notification) => ({ key: `notice-${notification.id}`, title: notification.title, meta: notification.message, onDismiss: () => markNotificationRead(notification.id) }));
  const needsYou = [...tiles, ...notices];
  const nextGuide = guidesFor(user).find((guide) => !(user.learning?.guides_done || []).includes(guide.id));
  const recentQueries = uniqueByText(userQueries).slice(0, 3);
  const statusChip = {
    resolved: ['Solved', 'qflow-home-pill-good'],
    unresolved: ['Needs follow-up', 'qflow-home-pill-warn'],
    answered: ['Answered', 'qflow-home-pill-good'],
    pending_feedback: ['To rate', 'qflow-home-pill-warn'],
  };
  const admin = homeSummary.admin;
  const primaryRole = homeSummary.roles?.[0];
  const teamLine = [
    primaryRole ? `${primaryRole.title}${primaryRole.team_name ? ` · ${primaryRole.team_name}` : ''}` : homeSummary.teams?.map((team) => team.name).join(', '),
    homeSummary.roles?.length > 1 ? `+${homeSummary.roles.length - 1} more role${homeSummary.roles.length === 2 ? '' : 's'}` : null,
    profileSummary.leadership?.is_source_leader ? 'Source Leader' : null,
    profileSummary.leadership?.is_floor_leader ? 'Floor Leader' : null,
  ].filter(Boolean).join(' · ');

  const teamNames = snapshot?.scope_teams || [];
  const scopeLabel = snapshot?.scope === 'organisation' ? 'Your organisation'
    : snapshot?.scope === 'teams' ? (teamNames.length > 2 ? `${teamNames.length} teams` : teamNames.join(' and ') || 'Your teams')
    : 'Your numbers';

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard-page">
      <div className="mx-auto max-w-5xl space-y-5 px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">{greeting()}, {(user.name || '').split(' ')[0] || 'there'}</h1>
            <p className="mt-1 text-sm text-gray-500">{[new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' }), scopeLabel, 'last 30 days'].join(' · ')}</p>
          </div>
          {snapshot?.available_scopes?.length > 1 && <div className="ml-auto inline-flex rounded-xl border p-1" style={{ borderColor: 'var(--qflow-border)' }} role="group" aria-label="Show numbers for" data-testid="home-scope">
            {snapshot.available_scopes.map((value) => <button key={value} type="button" aria-pressed={snapshot.scope === value} onClick={() => chooseScope(value)} className={`rounded-lg px-3 py-1 text-xs font-semibold ${snapshot.scope === value ? 'qflow-accent-bg' : 'text-gray-500'}`} data-testid={`home-scope-${value}`}>{SCOPE_NAMES[value]}</button>)}
          </div>}
          <span className={`qflow-home-pill ${needsYou.length ? 'qflow-home-pill-good' : 'qflow-home-chip'}`}>{needsYou.length ? plural(needsYou.length, 'thing needs you', 'things need you') : 'You’re all caught up'}</span>
        </div>

        {snapshotError && <p role="alert" className="qflow-home-card p-4 text-sm" data-testid="snapshot-error">Your snapshot could not be loaded. Refresh to try again, or open Analytics for detailed reporting.</p>}

        {/* Snapshot: how well questions are being resolved, and what is waiting. Asking lives in Ask Qflow and the / quick ask. */}
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-kpis" aria-label="Last 30 days">
          <Kpi label="Resolution rate" value={snapshot?.resolution_rate?.current} suffix="%" previous={snapshot?.resolution_rate?.previous} unit=" pts" hint="Of rated answers" />
          <Kpi label="Questions asked" value={snapshot?.questions?.current} previous={snapshot?.questions?.previous} percent />
          <Kpi label="Open escalations · all dates" value={snapshot?.escalations?.open} note={!snapshot ? 'Not available yet' : snapshot.escalations.overdue ? `${snapshot.escalations.overdue} waiting over a day` : 'None waiting long'} warn={snapshot?.escalations?.overdue > 0} onClick={snapshot?.escalations?.open ? scrollToQueue : undefined} />
          <Kpi label="Time to resolve" value={snapshot?.hours_to_resolve?.current} suffix="h" previous={snapshot?.hours_to_resolve?.previous} unit="h" lowerIsBetter hint="Escalations, median" />
        </section>

        <div className="grid gap-4 lg:grid-cols-5">
          <section className="qflow-home-card p-5 lg:col-span-3" data-testid="home-trend">
            <div className="flex items-baseline justify-between"><h2 className="text-base font-bold">Resolution rate, last 8 weeks</h2><button type="button" className="qflow-accent-text text-xs font-semibold hover:underline" onClick={() => navigate('/analytics')}>Analytics</button></div>
            <TrendLine points={snapshot?.trend || []} />
            {snapshot?.outcomes?.answered > 0 && <p className="mt-2 text-xs text-gray-500">{[`${snapshot.outcomes.resolved} solved`, `${snapshot.outcomes.not_solved} not solved`, `${snapshot.outcomes.awaiting_rating} waiting for a rating`].join(' · ')}</p>}
          </section>
          <section className="qflow-home-card p-5 lg:col-span-2" data-testid="home-needs-you" aria-label="Needs you">
            <h2 className="text-base font-bold">Needs you</h2>
            {needsYou.length ? <ul className="mt-2 divide-y" style={{ borderColor: 'var(--qflow-border)' }}>
              {tiles.map((tile) => <li key={tile.key} className="flex items-center gap-3 py-2.5" data-testid={`home-tile-${tile.key}`}>
                <span className="qflow-home-number w-12 shrink-0 text-xl" style={tile.warn ? { color: '#e0a052' } : undefined}>{tile.count}</span>
                <span className="min-w-0 flex-1 text-sm">{tile.label}</span>
                <button type="button" onClick={tile.onAction} className="qflow-home-chip shrink-0 px-3 py-1 text-xs font-semibold">{tile.action}</button>
              </li>)}
              {notices.map((notice) => <li key={notice.key} className="flex items-center gap-3 py-2.5">
                <Bell size={16} className="qflow-accent-text w-12 shrink-0" aria-hidden="true" />
                <div className="min-w-0 flex-1"><p className="truncate text-sm">{notice.title}</p>{notice.meta && <p className="line-clamp-1 text-xs text-gray-500">{notice.meta}</p>}</div>
                <button type="button" className="shrink-0 text-xs font-semibold text-gray-500 hover:underline" onClick={notice.onDismiss}>Dismiss</button>
              </li>)}
            </ul> : <p className="mt-2 text-sm text-gray-500">You’re all caught up.</p>}
            {ratingOpen && toRate.length > 0 && <ul className="mt-2 space-y-1 rounded-lg p-2" style={{ background: 'var(--qflow-app-bg)' }} data-testid="home-rating-list">
              {toRate.slice(0, 5).map((query) => <li key={query.id} className="flex flex-wrap items-center justify-between gap-2 px-1 py-1">
                <span className="min-w-0 flex-1 truncate text-sm">{query.query_text}</span>
                <ResolutionConfirmation queryId={query.id} status={query.status} compact onUpdated={(updated) => setUserQueries((current) => current.map((entry) => entry.id === query.id ? { ...entry, ...updated } : entry))} />
              </li>)}
            </ul>}
          </section>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <section className="qflow-home-card p-5" data-testid="home-unanswered">
            <h2 className="text-base font-bold">{snapshot?.scope === 'me' ? 'Your unanswered questions' : 'Top unanswered topics'}</h2>
            <p className="text-xs text-gray-500">Not solved in the last 30 days. Adding knowledge here helps most.</p>
            {snapshot?.unanswered_topics?.length ? <ul className="mt-2 divide-y" style={{ borderColor: 'var(--qflow-border)' }}>{snapshot.unanswered_topics.map((item) => <li key={item.topic} className="flex items-center justify-between gap-3 py-2 text-sm"><span className="min-w-0 truncate">{item.topic}</span><span className="shrink-0 text-xs text-gray-500">{plural(item.count, 'question', 'questions')}</span></li>)}</ul> : <p className="mt-3 text-sm text-gray-500">{snapshot ? 'No questions marked not solved in the last 30 days.' : 'No snapshot available yet.'}</p>}
          </section>
          <section className="qflow-home-card p-5" data-testid="home-escalations">
            <h2 className="text-base font-bold">{snapshot?.scope === 'me' ? 'Your escalations' : 'Escalations by team'}</h2>
            <p className="text-xs text-gray-500">Open now, most overdue first.</p>
            {snapshot?.escalations_by_team?.length ? <ul className="mt-2 divide-y" style={{ borderColor: 'var(--qflow-border)' }}>{snapshot.escalations_by_team.map((row, index) => <li key={`${index}-${row.team}`} className="flex items-center justify-between gap-3 py-2 text-sm"><span className="min-w-0 truncate">{row.team}</span><span className="shrink-0 text-xs">{row.open} open{row.overdue ? <span style={{ color: '#e0a052' }}> · {row.overdue} over a day</span> : ''}</span></li>)}</ul> : <p className="mt-3 text-sm text-gray-500">{snapshot ? 'No open escalations.' : 'No snapshot available yet.'}</p>}
          </section>
        </div>

        <div className="grid gap-4 lg:grid-cols-5">
          <section className="qflow-home-card p-5 lg:col-span-3" data-testid="home-recent-questions">
            <div className="mb-2 flex items-center justify-between"><h2 className="text-base font-bold">Pick up where you left off</h2>{userQueries.length > recentQueries.length && <button type="button" className="qflow-accent-text text-xs font-semibold hover:underline" onClick={() => openMetricModal('queries', 'My Queries', Search, 'sky', userQueries)}>View all</button>}</div>
            {recentQueries.length ? <ul className="divide-y" style={{ borderColor: 'var(--qflow-border)' }}>{recentQueries.map((query) => {
              const [label, chip] = statusChip[query.status] || statusChip.answered;
              return <li key={query.id} className="flex items-center gap-3 py-3"><div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{query.query_text}</p><p className="text-xs text-gray-500">{timeAgo(query.created_at)}</p></div><span className={`qflow-home-pill whitespace-nowrap ${chip}`}>{label}</span></li>;
            })}</ul> : <div className="py-6 text-sm text-gray-500">Your questions appear here once you’ve asked one. <button type="button" className="qflow-accent-text font-semibold hover:underline" onClick={() => navigate('/query')}>Ask your first question</button></div>}
          </section>
          <div className="space-y-4 lg:col-span-2">
            <section className="qflow-home-card p-5" data-testid="home-knowledge-updates">
              <h2 className="text-base font-bold">Fresh in your knowledge</h2>
              {homeSummary.knowledge_updates?.length ? <ul className="mt-3 space-y-3">{homeSummary.knowledge_updates.slice(0, 3).map((item) => <li key={item.id} className="flex items-start gap-3"><span className="qflow-home-icon grid h-9 w-9 shrink-0 place-items-center"><FileText size={16} aria-hidden="true" /></span><div className="min-w-0"><p className="truncate text-sm font-medium">{item.is_new ? 'New: ' : ''}{item.title}</p><p className="text-xs text-gray-500">{[item.team_name, item.version_label, timeAgo(item.updated_at)].filter(Boolean).join(' · ')}</p></div></li>)}</ul> : <p className="mt-2 text-sm text-gray-500">Nothing changed in your teams’ knowledge lately.</p>}
            </section>
            {admin && <section className="qflow-home-card p-5" data-testid="home-admin-tasks">
              <h2 className="text-base font-bold">Organisation</h2>
              <ul className="mt-2 space-y-2 text-sm">
                <li className="flex items-center gap-3"><span className="flex-1">{plural(admin.vacant_roles, 'vacant role', 'vacant roles')} · {admin.unplaced_roles} unplaced</span><button type="button" className="qflow-accent-text text-xs font-semibold hover:underline" onClick={() => navigate('/org-chart')}>Org Chart</button></li>
                {admin.resolution_rate !== null && admin.resolution_rate !== undefined && <li className="flex items-center gap-3"><span className="flex-1">{admin.resolution_rate}% resolved in 30 days</span><button type="button" className="qflow-accent-text text-xs font-semibold hover:underline" onClick={() => navigate('/analytics')}>Analytics</button></li>}
              </ul>
            </section>}
          </div>
        </div>

        <section className="qflow-home-card flex flex-wrap items-center gap-4 p-5" data-testid="home-learn">
          <span className="qflow-home-icon grid h-11 w-11 shrink-0 place-items-center"><GraduationCap size={20} aria-hidden="true" /></span>
          <div className="min-w-[12rem] flex-1">
            <p className="qflow-home-eyebrow">Learn Qflow</p>
            <p className="mt-0.5 text-sm font-semibold">{nextGuide ? `Next: ${nextGuide.title}` : 'You’ve finished every guide for your role'}</p>
            <p className="text-xs text-gray-500">{nextGuide ? `${nextGuide.summary} · ${nextGuide.minutes} min` : 'Replay the welcome tour any time.'}</p>
          </div>
          <div className="flex gap-2">
            <button type="button" className="qflow-home-chip px-3 py-1.5 text-xs font-semibold" onClick={() => startGuide(nextGuide ? nextGuide.id : 'welcome')} data-testid="home-learn-show-me">Show me</button>
            <button type="button" className="qflow-accent-text px-2 text-xs font-semibold hover:underline" onClick={() => navigate('/getting-started')}>Explore all</button>
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs text-gray-500" data-testid="dashboard-profile-summary">
          <span className="flex items-center gap-1.5"><Users size={14} aria-hidden="true" />{teamLine || 'No Org Chart role yet'}</span>
          <button type="button" className="qflow-accent-text ml-auto font-semibold hover:underline" onClick={() => setPasswordDialog((current) => ({ ...current, open: true }))} data-testid="open-change-password">Change password</button>
        </div>

        {/* My Access Panel — shown to restricted Source Leaders so they know what they can edit/delete */}
        {accessSummary && !accessSummary.unrestricted && accessSummary.is_source_lead && (
          <Card className="p-6 mb-8 border-2 border-indigo-200 bg-indigo-50/40" data-testid="my-access-panel">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Shield className="text-indigo-600" size={22} />
                <span>My Access</span>
                <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-medium">Source Lead</span>
              </h2>
              <Button
                variant="outline"
                size="sm"
                className="text-xs"
                disabled
                title="Contact your Admin to request additional access"
                data-testid="request-access-btn"
              >
                <Lock size={12} className="mr-1" /> Request Access
              </Button>
            </div>
            <p className="text-xs text-gray-600 mb-4">
              These are the folders and files you can manage in the Knowledge Hub. Folder permissions are the default; file-level permissions override them.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Folders */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <FolderOpen size={12} /> Folders ({accessSummary.folders?.length || 0})
                </h3>
                {accessSummary.folders?.length ? (
                  <div className="space-y-1.5 max-h-56 overflow-y-auto">
                    {accessSummary.folders.map((f, i) => (
                      <div key={f.folder_id || i} className="p-2 bg-white rounded border border-indigo-100 flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <FolderOpen size={14} className="text-indigo-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-800 truncate">{f.name || f.folder_id}</span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">View</span>
                          {f.can_edit && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Edit</span>}
                          {f.can_delete && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Delete</span>}
                          {!f.can_edit && !f.can_delete && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 italic">Read-only</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 italic">No folder access granted yet.</p>
                )}
              </div>

              {/* Files (overrides) */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <FileText size={12} /> File Overrides ({accessSummary.files?.length || 0})
                </h3>
                {accessSummary.files?.length ? (
                  <div className="space-y-1.5 max-h-56 overflow-y-auto">
                    {accessSummary.files.map((f, i) => (
                      <div key={f.file_id || i} className="p-2 bg-white rounded border border-indigo-100 flex items-center justify-between">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText size={14} className="text-indigo-500 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{f.title}</p>
                            {f.parent_folder && (
                              <p className="text-[10px] text-gray-500 truncate">in {f.parent_folder}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {f.is_override && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium" title="File-level permission overrides the folder default">Override</span>
                          )}
                          {f.can_edit && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">Edit</span>}
                          {f.can_delete && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Delete</span>}
                          {!f.can_edit && !f.can_delete && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 italic">Read-only</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 italic">No file-level overrides.</p>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Escalated queue for Source Leads / Floor Leaders / Managers */}
        {(queryCardRoles.is_source_lead || queryCardRoles.is_floor_leader || queryCardRoles.is_admin) && (
          <Card id="escalated-queue" className="p-4 border border-amber-200 bg-amber-50/40">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center space-x-2">
                <Flag className="text-amber-600" size={20} />
                <span>Escalated Queries Queue</span>
                {queryCardRoles.is_source_lead && (
                  <span className="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">Source Lead</span>
                )}
                {queryCardRoles.is_floor_leader && !queryCardRoles.is_source_lead && (
                  <span className="ml-2 px-2 py-0.5 bg-sky-100 text-sky-700 text-xs rounded-full">Floor Leader</span>
                )}
                {queryCardRoles.is_source_lead && queryCardRoles.is_floor_leader && (
                  <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full">Hybrid</span>
                )}
                {queryCardRoles.is_admin && !queryCardRoles.is_source_lead && (
                  <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Manager</span>
                )}
              </h2>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant={myQueue ? 'default' : 'outline'}
                  onClick={() => setMyQueue((current) => !current)}
                  data-testid="my-query-queue-toggle"
                >
                  <User size={13} className="mr-1" /> {myQueue ? 'My queue' : 'Full queue'}
                </Button>
                <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                  {queryCardCounts.flagged || 0} Flagged
                </span>
                <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                  {queryCardCounts.escalations || 0} Escalated
                </span>
              </div>
            </div>
            
            {queryCardCounts.total === 0 ? (
              <div className="text-center py-6">
                <AlertCircle className="mx-auto text-gray-400 mb-2" size={32} />
                <p className="text-gray-500">No query cards to review. Great job!</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="query-card-list">
                {queryCards.slice(0, 5).map((card) => {
                  const isEscalation = card.card_type === 'escalation';
                  return (
                    <div key={card.id} className={`p-3 rounded-md border ${isEscalation ? 'bg-orange-50/60 border-orange-200' : 'bg-white border-amber-200'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 line-clamp-2">{card.query_text}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                            <span className={`px-2 py-0.5 rounded font-medium ${isEscalation ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'}`}>
                              {card.source}
                            </span>
                            {card.owner_name && (
                              <span className="flex items-center gap-1">
                                <User size={12} /> Raised by {card.owner_name}
                              </span>
                            )}
                            {isEscalation && (card.assigned_to_user ? (
                              <span className="flex items-center gap-1.5" data-testid={`query-assignee-${card.id}`}>
                                {card.assigned_to_user.photo_url ? (
                                  <img src={card.assigned_to_user.photo_url} alt="" className="h-5 w-5 rounded-full object-cover" />
                                ) : (
                                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-[9px] font-semibold text-indigo-700">
                                    {(card.assigned_to_user.name || '?').split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase()}
                                  </span>
                                )}
                                {card.assigned_to_user.name}
                              </span>
                            ) : (
                              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-gray-600" data-testid={`query-unassigned-${card.id}`}>Unassigned</span>
                            ))}
                            <span>•</span>
                            <span>{card.created_at ? new Date(card.created_at).toLocaleDateString() : '—'}</span>
                            {card.knowledge_coverage && (
                              <>
                                <span>•</span>
                                <span className="capitalize">{card.knowledge_coverage} coverage</span>
                              </>
                            )}
                          </div>
                          {card.reason && (
                            <p className="text-sm text-gray-600 mt-2 italic line-clamp-2">&quot;{card.reason}&quot;</p>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {card.health_flag && (
                            <span className="rounded px-2 py-1 text-xs font-medium capitalize bg-gray-100 text-gray-700">
                              {card.health_flag.replace('_', ' ')}
                            </span>
                          )}
                          <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                            card.status === 'Resolved' || card.status === 'resolved' ? 'bg-green-100 text-green-700'
                            : card.status === 'Assigned' || card.status === 'In Progress' || card.status === 'in_progress' ? 'bg-blue-100 text-blue-700'
                            : 'bg-amber-100 text-amber-700'
                          }`}>
                            {card.status || 'Unassigned'}
                          </span>
                          {isEscalation && queryCardRoles.can_manage_assignments && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setAssignmentDialog({ open: true, card })}
                              data-testid={`query-assignment-control-${card.id}`}
                            >
                              {card.assigned_to ? 'Reassign' : 'Assign'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {queryCardCounts.total > 5 && (
                  <p className="text-center text-sm text-amber-600">
                    +{queryCardCounts.total - 5} more query cards
                  </p>
                )}
              </div>
            )}
          </Card>
        )}
      </div>

      <AssignmentDialog
        open={assignmentDialog.open}
        onOpenChange={(open) => setAssignmentDialog((current) => ({ ...current, open }))}
        card={assignmentDialog.card}
        users={assignmentUsers}
        onAssign={handleAssignQuery}
        saving={assignmentSaving}
      />
      <Dialog open={passwordDialog.open} onOpenChange={(open) => setPasswordDialog((current) => ({ ...current, open }))}>
        <DialogContent className="max-w-md" data-testid="change-password-dialog">
          <DialogHeader><DialogTitle>Change password</DialogTitle></DialogHeader>
          <form className="space-y-4" onSubmit={handlePasswordChange}>
            <div><label className="mb-1 block text-sm font-medium">Current password</label><Input type="password" autoComplete="current-password" value={passwordDialog.current} onChange={(event) => setPasswordDialog((current) => ({ ...current, current: event.target.value }))} required /></div>
            <div><label className="mb-1 block text-sm font-medium">New password</label><Input type="password" autoComplete="new-password" minLength={8} value={passwordDialog.next} onChange={(event) => setPasswordDialog((current) => ({ ...current, next: event.target.value }))} required /><p className="mt-1 text-xs text-gray-500">At least 8 characters.</p></div>
            <div><label className="mb-1 block text-sm font-medium">Confirm new password</label><Input type="password" autoComplete="new-password" minLength={8} value={passwordDialog.confirm} onChange={(event) => setPasswordDialog((current) => ({ ...current, confirm: event.target.value }))} required /></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setPasswordDialog({ open: false, current: '', next: '', confirm: '', saving: false })}>Cancel</Button><Button type="submit" disabled={passwordDialog.saving}>{passwordDialog.saving ? 'Updating...' : 'Update password'}</Button></div>
          </form>
        </DialogContent>
      </Dialog>
      {/* Metric Detail Modal */}
      <MetricDetailModal
        isOpen={metricModal.isOpen}
        onClose={() => setMetricModal({ ...metricModal, isOpen: false })}
        title={metricModal.title}
        icon={metricModal.icon}
        color={metricModal.color}
        data={metricModal.data}
        type={metricModal.type}
        onNavigate={navigate}
      />

    </div>
  );
};

export default Dashboard;
